import { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Snackbar,
  Typography,
  useTheme
} from '@mui/material';
import StarsOutlined from '@mui/icons-material/StarsOutlined';
import CardGiftcardOutlined from '@mui/icons-material/CardGiftcardOutlined';
import { useTranslation } from 'react-i18next';
import AbilityContext from '@/contexts/AbilityContext';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { Action } from '@/types/permissions';
import { customer360Service } from '@/data/customer360Service';
import type {
  CustomerSegment,
  CustomerSummary
} from '@/types/Customer360.interface';
import { formatMoney } from '@/utils/salesReport';
import { formatDateToDayMonthYear } from '@/utils/dateFormatters';
import { AddPointsDialog, GrantVoucherDialog } from './CustomerActionDialogs';

const SEGMENT_COLOR: Record<
  CustomerSegment,
  'success' | 'primary' | 'info' | 'secondary' | 'warning' | 'default'
> = {
  loyal: 'success',
  regular: 'primary',
  occasional: 'info',
  new: 'secondary',
  atRisk: 'warning',
  inactive: 'default'
};

interface CustomerSummaryCardsProps {
  userId: number;
  customerName: string;
  /** Called after points or a voucher were given (refresh other tabs). */
  onChanged?: () => void;
}

/**
 * The customer at a glance: spend, visits, last visit, segment, points,
 * vouchers and membership; plus "Puan ekle" / "Kupon hediye et" in branch
 * view for admins who may manage loyalty.
 */
const CustomerSummaryCards = ({
  userId,
  customerName,
  onChanged
}: CustomerSummaryCardsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const ability = useContext(AbilityContext);
  const { currentBranch, isAdminView } = useUserViewMode();
  const [summary, setSummary] = useState<CustomerSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [dialog, setDialog] = useState<'points' | 'voucher' | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    customer360Service
      .getSummary(userId)
      .then((result) => {
        if (!cancelled) {
          setSummary(result);
          setFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, reload]);

  if (failed) return null;
  if (!summary) return null;

  const branch = !isAdminView ? currentBranch : null;
  const canGive = Boolean(branch) && ability.can(Action.Create, 'Loyalty');

  const lastVisit =
    summary.daysSinceLastVisit === null
      ? t('customerCard.never')
      : summary.daysSinceLastVisit === 0
        ? t('customerCard.today')
        : t('customerCard.daysAgo', { n: summary.daysSinceLastVisit });

  const tiles = [
    {
      key: 'spent',
      label: t('customerCard.totalSpent'),
      value: formatMoney(summary.totalSpent),
      sub: t('customerCard.orders', { n: summary.orderCount })
    },
    {
      key: 'basket',
      label: t('customerCard.averageBasket'),
      value: formatMoney(summary.averageBasket),
      sub: summary.firstOrderAt
        ? t('customerCard.since', {
            date: formatDateToDayMonthYear(summary.firstOrderAt)
          })
        : '—'
    },
    {
      key: 'visit',
      label: t('customerCard.lastVisit'),
      value: lastVisit,
      sub: t('customerCard.entries', { n: summary.entryCount })
    },
    {
      key: 'points',
      label: t('customerCard.points.label'),
      value: summary.points.current.toLocaleString('tr-TR'),
      sub: t('customerCard.points.lifetime', {
        n: summary.points.lifetime.toLocaleString('tr-TR')
      })
    },
    {
      key: 'vouchers',
      label: t('customerCard.vouchers'),
      value: String(summary.activeVoucherCount),
      sub: t('customerCard.vouchersActive')
    },
    {
      key: 'membership',
      label: t('customerCard.membership'),
      value: summary.membership?.planName ?? t('customerCard.noMembership'),
      sub: summary.membership
        ? t('customerCard.membershipEnds', {
            date: formatDateToDayMonthYear(summary.membership.endDate),
            n: summary.membership.remainingDays
          })
        : '—'
    }
  ];

  const handleDone = (message: string) => {
    setDone(message);
    setReload((value) => value + 1);
    onChanged?.();
  };

  return (
    <Card sx={{ p: 2.5, mb: 3 }} data-testid="customer-summary">
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
        <Typography variant="h4">{t('customerCard.title')}</Typography>
        <Chip
          size="small"
          color={SEGMENT_COLOR[summary.segment]}
          label={t(`customerCard.segment.${summary.segment}`)}
        />
        <Box flex={1} />
        {canGive && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<StarsOutlined />}
              onClick={() => setDialog('points')}
            >
              {t('customerCard.points.title')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CardGiftcardOutlined />}
              onClick={() => setDialog('voucher')}
            >
              {t('customerCard.voucher.title')}
            </Button>
          </>
        )}
      </Box>
      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid item xs={6} md={4} lg={2} key={tile.key}>
            <Box
              sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {tile.label}
              </Typography>
              <Typography
                variant="h4"
                sx={{ my: 0.5 }}
                noWrap
                title={tile.value}
              >
                {tile.value}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'none' }}
              >
                {tile.sub}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      {branch && (
        <>
          <AddPointsDialog
            open={dialog === 'points'}
            onClose={() => setDialog(null)}
            onDone={handleDone}
            userId={userId}
            branchId={branch.id}
            branchName={branch.name}
            customerName={customerName}
          />
          <GrantVoucherDialog
            open={dialog === 'voucher'}
            onClose={() => setDialog(null)}
            onDone={handleDone}
            userId={userId}
            branchId={branch.id}
            branchName={branch.name}
            customerName={customerName}
          />
        </>
      )}
      <Snackbar
        open={done !== null}
        autoHideDuration={6000}
        onClose={() => setDone(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {done ? (
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setDone(null)}
          >
            {done}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Card>
  );
};

export default CustomerSummaryCards;
